import {
    Challenge,
    ConditionalRender,
    CoreProvider,
    eventBus,
    getDeviceType,
    getLocale,
    isShowChallenge,
    makeWidgetsList,
    selectRootWidgetUids,
    setTranslations,
    useAppSelector,
    usePageTransition,
} from '@libs/core';
import { EDeviceType, SystemEventTypeEnum } from '@libs/types';
import React, { useEffect, useLayoutEffect, ViewTransition } from 'react';
import translations from '../translations.json';
import { Offline } from './common/Offline';
import { PortalWrapper } from './common/PortalWrapper';
import './theme/general.css';
import './theme/tokens.v2.css';
import { PAGE_TRANSITION_CLASSES, pageTransitionStyles, resolvePageTransitionTypes } from './pageTransition/pageTransition';
import Widget from './Widget';
import { WidgetWrapper } from './WidgetWrapper';

setTranslations(translations);

const WidgetsList = makeWidgetsList(Widget);

// Scenario page transitions (uiEffects), phones only: the resolver picks the transition type per page change,
// pageTransition/pageTransitions.css styles it. `update` (not a `key` swap) keeps the page tree mounted, so widgets
// are reconciled by uid.
const PAGE_UPDATE = { ...PAGE_TRANSITION_CLASSES, default: 'none' };
const pageTransitionResolver = getDeviceType() === EDeviceType.Mobile ? resolvePageTransitionTypes : undefined;

export function App() {
    const rootWidgetUids = useAppSelector(selectRootWidgetUids);
    const statusShowChallenge = useAppSelector(isShowChallenge);
    usePageTransition(pageTransitionResolver);

    // useLayoutEffect (not useEffect) on purpose: it runs synchronously before the first paint,
    // so the `lang` attribute is set on <html> before widgets render. This ensures the `:lang(...)`
    // CSS selectors that switch font-family (GT Standard / GT America / Noto Sans SC) match on the
    // first frame and avoids a visible font flash and re-layout once widgets are already mounted.
    useLayoutEffect(() => {
        const locale = getLocale();
        if (locale) {
            document.documentElement.lang = locale;
        }
    }, []);

    useEffect(() => {
        let documentScrolled = false;
        const handleScroll = () => {
            const scrolled = window.scrollY !== 0;
            if (documentScrolled !== scrolled) {
                documentScrolled = scrolled;
                eventBus.emitGeneral(SystemEventTypeEnum.SCROLLED, scrolled);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <CoreProvider>
            <Offline />
            <Challenge />
            <ConditionalRender condition={!statusShowChallenge}>
                <PortalWrapper>
                    <WidgetWrapper>
                        <ViewTransition update={PAGE_UPDATE} default="none">
                            <div className={pageTransitionStyles.page}>
                                <WidgetsList uids={rootWidgetUids} />
                            </div>
                        </ViewTransition>
                    </WidgetWrapper>
                </PortalWrapper>
            </ConditionalRender>
        </CoreProvider>
    );
}
